import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
} from '@angular/material/dialog';
import { CoordinationResultStudyDetail } from '../../models/coordination-result-study-detail';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { Subject } from 'rxjs';
import { GridHelper } from '../../../../../../core/models/grid/grid-helper';
import { CoordinationResultStudyDetailModalData } from './models/coordination-result-study-detail-modal-data';
import { MatButtonModule } from '@angular/material/button';
import { GridToolbarOptions } from '../../../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { GridToolbarComponent } from '../../../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { MultiSelectFilterComponent } from '../../../../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { FilterOption } from '../../../../../../core/models/grid/filter-option';
import { PassOrFailEnum } from '../../constants/pass-or-fail-enum';
import { FrequencyFormattingService } from '../../../../../../core/services/frequency-formatting/frequency-formatting.service';

@Component({
    selector: 'app-coordination-results-frequency',
    standalone: true,
    imports: [
        AgGridAngular,
        DraggableModalWrapperComponent,
        GridToolbarComponent,
        MatButtonModule,
        MatDialogActions,
        MatDialogClose,
        MatDialogContent,
        MatDialogTitle,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './coordination-results-frequency.component.html',
})
export class CoordinationResultsFrequencyComponent
    implements OnInit, OnDestroy
{
    destroy$ = new Subject<void>();

    rowData: CoordinationResultStudyDetail[] = [];
    gridApi: GridApi | undefined;
    passOrFailFilterOptions: FilterOption[] = [
        { label: 'Pass', value: PassOrFailEnum.Pass, selected: false },
        { label: 'Fail', value: PassOrFailEnum.Fail, selected: false },
    ];

    columnDefs: ColDef[] = [
        {
            headerName: 'Frequency',
            field: nameOf<CoordinationResultStudyDetail>('incumFreq'),
            filter: true,
            minWidth: 125,
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Frequency',
            },
            pinned: 'left',
            valueFormatter: (params) => {
                return params.value != null
                    ? this.frequencyFormattingService.getFormattedFrequency(
                          params.value,
                      )
                    : '';
            },
        },
        {
            headerName: 'Call Sign',
            wrapHeaderText: true,
            field: nameOf<CoordinationResultStudyDetail>('callSign'),
            filter: true,
            minWidth: 100,
            pinned: 'left',
        },
        {
            headerName: 'Applicant Name',
            field: nameOf<CoordinationResultStudyDetail>('licenseeName'),
            filter: true,
            wrapHeaderText: true,
            cellStyle: {
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1.6,
            },
            pinned: 'left',
        },
        {
            headerName: 'Station Class',
            field: nameOf<CoordinationResultStudyDetail>('stationClass'),
            filter: true,
            wrapHeaderText: true,
            minWidth: 110,
            headerStyle: {
                textAlign: 'center',
            },
        },
        {
            headerName: 'Service Class',
            field: nameOf<CoordinationResultStudyDetail>('serviceClass'),
            filter: true,
            wrapHeaderText: true,
            minWidth: 110,
            headerStyle: {
                textAlign: 'center',
            },
        },
        {
            headerName: 'Distance',
            wrapHeaderText: true,
            field: nameOf<CoordinationResultStudyDetail>('distance'),
            filter: true,
            minWidth: 115,
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Distance',
            },
        },
        {
            headerName: 'Overlap %',
            field: nameOf<CoordinationResultStudyDetail>('ovpIP'),
            filter: true,
            wrapHeaderText: true,
            minWidth: 110,
            headerStyle: {
                textAlign: 'center',
            },
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Overlap %',
            },
        },
        {
            headerName: 'Incumbent Overlap',
            field: nameOf<CoordinationResultStudyDetail>('ovpI'),
            filter: true,
            wrapHeaderText: true,
            minWidth: 150,
            headerStyle: {
                textAlign: 'center',
            },
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Incumbent Ovp',
            },
        },
        {
            headerName: 'Proposed Overlap',
            field: nameOf<CoordinationResultStudyDetail>('ovpP'),
            filter: true,
            wrapHeaderText: true,
            minWidth: 125,
            headerStyle: {
                textAlign: 'center',
            },
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Proposed Ovp',
            },
        },
        {
            headerName: 'ERP',
            field: nameOf<CoordinationResultStudyDetail>('erp'),
            filter: true,
            minWidth: 80,
        },
        {
            headerName: 'Emission',
            field: nameOf<CoordinationResultStudyDetail>('emissionCode'),
            filter: true,
            minWidth: 120,
        },
        {
            headerValueGetter: () => {
                return window.innerWidth < 600 ? 'Pass/\nFail' : 'Pass/Fail';
            },
            field: nameOf<CoordinationResultStudyDetail>('passFail'),
            filter: MultiSelectFilterComponent,
            filterParams: {
                filterOptions: this.passOrFailFilterOptions,
            },
            wrapHeaderText: true,
            minWidth: 90,
            headerStyle: {
                textAlign: 'center',
            },
        },
    ];

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        columnDefs: this.columnDefs,
        paginationPageSize: 50,
    };

    gridToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => {},
        quickFilter: false,
        clearFilters: true,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<CoordinationResultStudyDetail>('incumFreq'),
                nameOf<CoordinationResultStudyDetail>('callSign'),
                nameOf<CoordinationResultStudyDetail>('licenseeName'),
                nameOf<CoordinationResultStudyDetail>('stationClass'),
                nameOf<CoordinationResultStudyDetail>('serviceClass'),
                nameOf<CoordinationResultStudyDetail>('distance'),
                nameOf<CoordinationResultStudyDetail>('ovpIP'),
                nameOf<CoordinationResultStudyDetail>('ovpI'),
                nameOf<CoordinationResultStudyDetail>('ovpP'),
                nameOf<CoordinationResultStudyDetail>('erp'),
                nameOf<CoordinationResultStudyDetail>('emissionCode'),
                nameOf<CoordinationResultStudyDetail>('passFail'),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    'Results_Frequency',
                    this.data.salmonLogId,
                ),
        },
    };

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private readonly data: CoordinationResultStudyDetailModalData,
        private readonly frequencyFormattingService: FrequencyFormattingService,
    ) {}

    ngOnInit(): void {
        this.rowData = this.data.details;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onGridReady(params: GridReadyEvent): void {
        this.gridApi = params.api;
    }
}
